import { useEffect, useState } from 'react';
import { getResizedImage } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { FormGroup } from 'components/common/form';
import { EditorBlockTranslationReference } from 'components/editor';
import { TextEditor } from 'components/editor/inputs';

interface Props {
  altTextVisible: boolean;
  blockId: number;
  imageAltText: string;
  imageAltTextReference?: string;
  imageCaption: string;
  imageCaptionReference?: string;
  imageUrl: string;
  imageWrapperHeight: number;
  imageWrapperRef: (el: HTMLDivElement) => void;
  isPreview?: boolean;
  onAltTextChange: (value: string) => void;
  onCaptionChange: (value: string) => void;
  onImageLoad: (ev: React.SyntheticEvent) => void;
  maxWidth: number;
  setSize: boolean;
}

const getImageSrcSet = (url: string, cacheBuster: string) => {
  const sizes = [1920, 1280, 960, 640, 480];
  return sizes
    .map((size) => `${getResizedImage({ url, size })}${cacheBuster} ${size}w`)
    .join(', ');
};

const EditorBlockImageContent = ({
  altTextVisible,
  blockId,
  imageAltText,
  imageAltTextReference,
  imageCaption,
  imageCaptionReference,
  imageUrl,
  imageWrapperHeight,
  imageWrapperRef,
  isPreview = false,
  onAltTextChange,
  onCaptionChange,
  onImageLoad,
  maxWidth,
  setSize
}: Props) => {
  const { messages } = useReduxState();

  const [cacheKey, setCacheKey] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Crop returns a new URL before resized srcSet files may exist.
  // Safari can cache the first 403, so retries must change the URL.
  // Reset state for each new image URL, then use cacheKey-based query params
  // to force fresh requests until resized files are available.
  useEffect(() => {
    setImageLoaded(false);
    setImageLoadErrors(0);
    setCacheKey(0);
  }, [imageUrl]);

  const cacheBuster = cacheKey > 0 ? `?v=${cacheKey}` : '';

  const imageError: React.ReactEventHandler<HTMLImageElement> = (ev) => {
    if (imageLoadErrors > 10) return imageLoad(ev);

    setImageLoadErrors((state) => state + 1);

    // Change query param per retry so Safari does not reuse cached 403
    setTimeout(() => {
      setCacheKey((prev) => prev + 1);
    }, 1000);
  };

  const imageLoad: React.ReactEventHandler<HTMLImageElement> = (ev) => {
    if (imageLoaded) return;
    onImageLoad(ev);

    // add an empty string to src to force reload images on safari
    // apparently safari has a bug where it doesn't reload images
    // after src change when srcSet is used, but using outerHTML breaks styling
    // https://stackoverflow.com/questions/45487105/ajax-loaded-images-in-safari-not-respecting-srcset
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      const target = ev.target as HTMLImageElement;
      target.src += ' ';
      setImageLoaded(true);
    } else setImageLoaded(true);

    const eventData = { bubbles: true, detail: { blockId } };
    document.dispatchEvent(new CustomEvent('editor_image_loaded', eventData));
  };

  return (
    <div className="image">
      <div
        className="image-wrapper"
        ref={imageWrapperRef}
        style={{ height: setSize ? imageWrapperHeight : 'auto' }}
      >
        <img
          alt={imageAltText}
          onError={imageError}
          onLoad={imageLoad}
          sizes="100vw"
          src={imageUrl + cacheBuster}
          srcSet={getImageSrcSet(imageUrl, cacheBuster)}
          style={{ maxWidth: `${maxWidth}%` }}
        />
      </div>

      <div className="caption">
        {isPreview ? (
          <>
            <FormGroup>{imageCaption || ''}</FormGroup>

            {altTextVisible ? (
              <FormGroup
                label={messages.editorBlockImageAltText}
                maxWidth="100%"
              >
                {imageAltText || ''}
              </FormGroup>
            ) : null}
          </>
        ) : (
          <div>
            <EditorBlockTranslationReference
              isEnabled={Boolean(imageCaptionReference)}
              isInnerElement={true}
              referenceHtml={imageCaptionReference || ''}
              translationHtml={imageCaption}
            >
              <TextEditor
                content={imageCaption}
                isPlainText={true}
                onChange={onCaptionChange}
                placeholder={messages.editorBlockImageCaptionPlaceholder}
                showBorder={true}
              />
            </EditorBlockTranslationReference>

            {altTextVisible ? (
              <FormGroup
                label={messages.editorBlockImageAltText}
                maxWidth="100%"
              >
                <EditorBlockTranslationReference
                  isEnabled={Boolean(imageAltTextReference)}
                  isInnerElement={true}
                  referenceHtml={imageAltTextReference || ''}
                  translationHtml={imageAltText}
                >
                  <TextEditor
                    content={imageAltText}
                    isPlainText={true}
                    onChange={onAltTextChange}
                    placeholder={messages.editorBlockImageAltTextAdd}
                    showBorder={true}
                  />
                </EditorBlockTranslationReference>
              </FormGroup>
            ) : null}
          </div>
        )}
      </div>

      <style jsx>{`
        .image-wrapper {
          text-align: center;
        }
        .caption {
          margin-top: 5px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockImageContent;
